import { useAskInputActions, useAskInputState, } from '#pplx/frontend/apps/ask/ask-input/ask-input-provider/components/AskInputProviderShared.js';
import { COMPUTER_COMMAND_MENU_TRIGGER } from '#pplx/frontend/apps/ask/ask-input/ask-input-types/index.js';
import { ASK_INPUT_MENU } from '#pplx/frontend/apps/ask/types/askInputMenu.js';
import { useCallback, useEffect, useRef } from 'react';
export function useComputerCommandMenuState({ showSkills, isComputerMode, searchModeOverride, controlledSearchQuery, inputCommandQuery, open, onOpenChangeRequested, onInputValueChange, onInsertInputCommandQuery, onCommitInputCommandQuery, onMenuOpen, }) {
    const { activeMenu, inputRef } = useAskInputState();
    const { closeActiveMenuIfCurrent, onActiveMenuChange, setSuggestions } = useAskInputActions();
    const hadInputCommandQueryRef = useRef(false);
    const pendingComputerModeOpenRef = useRef(false);
    const suppressAutoInsertAfterQueryCloseRef = useRef(false);
    const canRequestComputerCommandMenu = showSkills;
    const canUseComputerCommandMenu = showSkills && isComputerMode;
    // TODO(DSYS-597): the controlled `open` isn't the sole source of truth here — the menu only
    // shows while it also owns `activeMenu` (the cross-menu mutex) and the input has an active
    // Lexical command query token. Making `open` authoritative means reworking the shared
    // `activeMenu` exclusion used by the sources/plus/search-mode menus.
    const effectiveOpen = canUseComputerCommandMenu && open && activeMenu === ASK_INPUT_MENU.COMPUTER_COMMAND_MENU;
    const searchQuery = controlledSearchQuery ?? (canUseComputerCommandMenu ? inputCommandQuery?.query : undefined);
    const requestMenuOpen = useCallback(() => {
        setSuggestions([]);
        if (activeMenu !== ASK_INPUT_MENU.COMPUTER_COMMAND_MENU) {
            onActiveMenuChange(ASK_INPUT_MENU.COMPUTER_COMMAND_MENU);
        }
        if (!open) {
            onOpenChangeRequested(true);
        }
    }, [activeMenu, onActiveMenuChange, onOpenChangeRequested, open, setSuggestions]);
    const requestMenuClose = useCallback(() => {
        closeActiveMenuIfCurrent(ASK_INPUT_MENU.COMPUTER_COMMAND_MENU);
        if (open) {
            onOpenChangeRequested(false);
        }
    }, [closeActiveMenuIfCurrent, onOpenChangeRequested, open]);
    useEffect(() => {
        if (canUseComputerCommandMenu)
            return;
        if (pendingComputerModeOpenRef.current &&
            canRequestComputerCommandMenu &&
            open &&
            inputCommandQuery === undefined) {
            return;
        }
        pendingComputerModeOpenRef.current = false;
        suppressAutoInsertAfterQueryCloseRef.current = false;
        onCommitInputCommandQuery();
        requestMenuClose();
    }, [
        canRequestComputerCommandMenu,
        canUseComputerCommandMenu,
        inputCommandQuery,
        onCommitInputCommandQuery,
        open,
        requestMenuClose,
    ]);
    useEffect(() => {
        if (!canUseComputerCommandMenu) {
            hadInputCommandQueryRef.current = false;
            return;
        }
        if (activeMenu !== null && activeMenu !== ASK_INPUT_MENU.COMPUTER_COMMAND_MENU) {
            // The incoming overlay already owns dismissal. Closing here would race its trigger's
            // pointer-down open and can replace that ownership with null before the click completes.
            return;
        }
        if (inputCommandQuery === undefined) {
            if (hadInputCommandQueryRef.current) {
                hadInputCommandQueryRef.current = false;
                suppressAutoInsertAfterQueryCloseRef.current = true;
                requestMenuClose();
            }
            return;
        }
        suppressAutoInsertAfterQueryCloseRef.current = false;
        hadInputCommandQueryRef.current = true;
        if (inputCommandQuery.source === 'slash' && !open) {
            onMenuOpen?.('slash');
        }
        requestMenuOpen();
    }, [
        activeMenu,
        canUseComputerCommandMenu,
        inputCommandQuery,
        onMenuOpen,
        open,
        requestMenuClose,
        requestMenuOpen,
    ]);
    const shouldSuppressAutosuggestions = useCallback((_query) => canUseComputerCommandMenu && inputCommandQuery !== undefined, [canUseComputerCommandMenu, inputCommandQuery]);
    const onInputChange = useCallback((nextValue, nextJson) => {
        onInputValueChange(nextValue, nextJson);
    }, [onInputValueChange]);
    const onTypeaheadTrigger = useCallback((trigger) => {
        if (trigger === COMPUTER_COMMAND_MENU_TRIGGER && canUseComputerCommandMenu)
            return false;
        return true;
    }, [canUseComputerCommandMenu]);
    const onTypeaheadSearchQueryChange = useCallback((_query) => { }, []);
    const openComputerCommandMenuAtCaret = useCallback(() => {
        if (!canUseComputerCommandMenu)
            return;
        pendingComputerModeOpenRef.current = false;
        onMenuOpen?.('toggle');
        onInsertInputCommandQuery();
        requestMenuOpen();
    }, [canUseComputerCommandMenu, onInsertInputCommandQuery, onMenuOpen, requestMenuOpen]);
    const onOpenChange = useCallback((nextOpen) => {
        const input = inputRef.current;
        if (nextOpen) {
            suppressAutoInsertAfterQueryCloseRef.current = false;
            input?.focus();
            if (!canUseComputerCommandMenu && canRequestComputerCommandMenu) {
                pendingComputerModeOpenRef.current = true;
                if (!open) {
                    onOpenChangeRequested(true);
                }
                return;
            }
            openComputerCommandMenuAtCaret();
        }
        else {
            pendingComputerModeOpenRef.current = false;
            suppressAutoInsertAfterQueryCloseRef.current = false;
            onCommitInputCommandQuery();
            requestMenuClose();
        }
    }, [
        canRequestComputerCommandMenu,
        canUseComputerCommandMenu,
        inputRef,
        onCommitInputCommandQuery,
        onOpenChangeRequested,
        open,
        openComputerCommandMenuAtCaret,
        requestMenuClose,
    ]);
    // Finishes an explicit toggle-open request that was started before Computer
    // mode became active. A controlled `open` value alone is not enough to create a
    // no-slash query, because the mode picker can also leave that state true while
    // switching into Computer mode.
    useEffect(() => {
        if (!open || !canUseComputerCommandMenu)
            return;
        if (activeMenu !== null && activeMenu !== ASK_INPUT_MENU.COMPUTER_COMMAND_MENU)
            return;
        if (inputCommandQuery !== undefined) {
            requestMenuOpen();
            return;
        }
        if (!pendingComputerModeOpenRef.current)
            return;
        if (suppressAutoInsertAfterQueryCloseRef.current)
            return;
        openComputerCommandMenuAtCaret();
    }, [
        activeMenu,
        canUseComputerCommandMenu,
        inputCommandQuery,
        open,
        openComputerCommandMenuAtCaret,
        requestMenuOpen,
    ]);
    return {
        open: effectiveOpen,
        searchQuery,
        searchModeOverride,
        canUseComputerCommandMenu,
        onInputChange,
        onOpenChange,
        onTypeaheadTrigger,
        onTypeaheadSearchQueryChange,
        shouldSuppressAutosuggestions,
    };
}
//# sourceMappingURL=useComputerCommandMenuState.js.map